import type { FC, ReactNode } from 'react'
import type {
    CurrencyCode,
    Invoice,
    Job,
    Staff
} from '@/types'

import Link from 'next/link'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Plate } from '@/components/kit/plate'
import { VehiclePhoto } from '@/components/kit/vehicle-photo'
import { JobStatusBadge, PaymentBadge } from '@/components/kit/badge'
import { JobMinutes } from '@/components/jobs/job-timing'
import { MINUTE, formatMinutes, formatMoney, formatTime } from '@/lib/carwash/format'
import { getElapsedMs } from '@/lib/carwash/selectors'
import { plateLabel } from '@/lib/carwash/plate'

interface JobRowProps {
    job: Job
    staff: Staff | null
    invoice: Invoice | null
    currency: CurrencyCode
    showDate?: string
}

/* One visit in a list: who, what, when, how long and whether it is paid. */
export const JobRow: FC<JobRowProps> = ({
    job,
    staff,
    invoice,
    currency,
    showDate
}): ReactNode => {
    const plate = plateLabel(job.plate, `#${job.number}`)
    const amount = invoice ? invoice.total : job.price

    let timing: ReactNode

    if (job.status === 'completed' && job.startedAt && job.completedAt) {
        timing = `${formatTime(job.startedAt)} to ${formatTime(job.completedAt)}, ${formatMinutes(getElapsedMs(job, 0) / MINUTE)}`
    }
    else if (job.status === 'cancelled') {
        timing = `Entered ${formatTime(job.enteredAt)}, cancelled`
    }
    else if (job.startedAt) {
        timing = (
            <>
                {`Started ${formatTime(job.startedAt)}, `}
                <JobMinutes job={job} />
            </>
        )
    }
    else {
        timing = (
            <>
                {`Entered ${formatTime(job.enteredAt)}, waiting `}
                <JobMinutes job={job} />
            </>
        )
    }

    return (
        <Link
            href={`/vehicles/${job.id}`}
            className={cn(
                'flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-card transition-[box-shadow,border-color] hover:border-slate-300 hover:shadow-raised focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                job.status === 'cancelled' && 'opacity-70'
            )}
        >
            <VehiclePhoto photo={job.photo} alt={`Photo of ${plate}`} className='size-14 rounded-xl' iconClassName='size-6' />

            <div className='min-w-0 flex-1'>
                <div className='flex items-center justify-between gap-2'>
                    <Plate value={job.plate} size='sm' />

                    <span className={cn('shrink-0 text-sm font-bold tabular-nums', job.status === 'cancelled' ? 'text-slate-400 line-through' : 'text-foreground')}>
                        {formatMoney(amount, currency)}
                    </span>
                </div>

                <div className='mt-1 flex items-center justify-between gap-2'>
                    <p className='min-w-0 truncate text-[13px] font-medium text-foreground'>
                        {[job.vehicleModel, job.serviceName].filter(Boolean).join(' · ')}
                    </p>

                    {job.status === 'completed' && invoice
                        ? <PaymentBadge status={invoice.status} />
                        : <JobStatusBadge status={job.status} />}
                </div>

                <p className='mt-0.5 truncate text-xs text-muted-foreground' suppressHydrationWarning>
                    {showDate && `${showDate}, `}
                    {staff ? `${staff.name}, ` : ''}
                    {timing}
                </p>
            </div>

            <ChevronRight className='size-4 shrink-0 text-slate-300' aria-hidden='true' />
        </Link>
    )
}
